import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SliceZone } from "@prismicio/react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, ButtonVariant } from "components/Form/Button";
import { Layout } from "components/Layout";
import { OrbQRCode } from "components/OrbQRCode";
import { SaveProgressModal } from "components/SaveProgressModal";
import { StepProgressOverview } from "components/StepProgressOverview";
import { taskInstructionSliceComponents } from "components/TaskInstructionSlices";
import { StepDetails } from "components/TaskInstructionSlices/StepDetails";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import {
  StepDetailsData,
  useMultiStepCapture,
} from "contexts/MultiStepCaptureContext";
import { useOrbQrContent } from "hooks/useOrbQrContent";
import { usePrivacyModal } from "hooks/usePrivacyModal";
import { useStepNavigation } from "hooks/useStepNavigation";
import { useTaskDefinition } from "hooks/useTaskDefinition";
import { getUserEmail } from "lib/get-user-email";
import { apiGetUserProfile } from "lib/api";
import {
  createOrbConfirmationFingerprint,
  getApplicableOrbDetailValues,
  getQrRetryDetail,
  getOrbStepLabelDetails,
  OrbDetailValue,
} from "lib/orb-qr";
import { TaskStep } from "lib/task-steps";
import { getTaskNavigationSteps } from "lib/task-navigation-qr";
import {
  getCompletedTaskSteps,
  saveCompletedTaskSteps,
} from "lib/task-progress";
import {
  hasChangedTaskPositions,
  getStoredWorkTaskSharedDetails,
  getWorkTaskReviewDestination,
  getWorkTaskStepSchemaFingerprint,
  getWorkTaskStepRoute,
  getWorkTaskStepRouteState,
  isStoredCaptureForWorkTaskStep,
  isWorkTaskStepComplete,
} from "lib/work-task-navigation";
import { getOrbStepDetailFields, ResolvedWorkTaskStep } from "lib/work-tasks";
import { AppRoutes, isQrCaptureType, QrCaptureType } from "types";

function fieldIds(step: ResolvedWorkTaskStep): string[] {
  return getOrbStepDetailFields(step).map(
    (field) => field.label_id?.toString().trim() || ""
  );
}

interface StepDetailValue extends OrbDetailValue {
  label_title: string;
  label_type: string;
}

export function StepOrb() {
  return <QrCaptureStep captureType="orb" />;
}

export function QrCaptureStep({ captureType }: { captureType: QrCaptureType }) {
  const captureLabel = captureType === "rtms" ? "RTMS" : "Orb";
  const [init, setInit] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [currentDetailValues, setCurrentDetailValues] = useState<
    StepDetailValue[]
  >([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [progressActionPending, setProgressActionPending] = useState(false);
  const [captureSaving, setCaptureSaving] = useState(false);
  const [isRedoing, setIsRedoing] = useState(false);
  const [shownDistributor, setShownDistributor] = useState<string>();

  const validateStepDetailsRef = useRef<
    (() => Array<{ id: string; title: string }>) | null
  >(null);
  const pendingStepDetailsSaveRef = useRef<Promise<void>>(Promise.resolve());
  const latestDetailValuesRef = useRef<StepDetailValue[]>([]);
  const editedStepDetailsRef = useRef<StepDetailsData | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const privacyModal = usePrivacyModal();
  const { config, contributor } = useContext(CollectionDetailsContext);
  const {
    stepCaptures,
    stepDetails,
    addStepCapture,
    addStepDetails,
    getStepDetails,
    getStepDetailsWithFields,
    currentTaskId,
    isLoadingSteps,
    setCurrentTaskId,
    clearAllCaptures,
  } = useMultiStepCapture();

  const stepNumber = Number(location.state?.stepNumber);
  const taskId = location.state?.taskId as string | undefined;
  const editMode = Boolean(location.state?.editMode);
  const navigationEntryId = location.state?.navigationEntryId as
    | string
    | undefined;
  const redoMode = Boolean(location.state?.redoMode);
  const reviewCompletedSteps = Boolean(location.state?.reviewCompletedSteps);
  // Distributor codes initialize Orb hardware; Zoom only scans the participant code.
  const distributorId =
    captureType === "orb"
      ? config?.selectedCollection?.data.distributor_id?.trim() || ""
      : "";
  const distributorKey = JSON.stringify([taskId, stepNumber, distributorId]);
  const distributorQrContent = /^[A-Za-z0-9._-]+$/.test(distributorId)
    ? `userid:${distributorId}:1::0::`
    : null;

  useEffect(() => {
    setShownDistributor(undefined);
  }, [taskId, stepNumber, distributorId]);
  const workTaskDocumentId = taskId
    ? config?.tasks[taskId]?.workTaskDocumentId
    : undefined;
  const {
    workTask: configuredWorkTask,
    orbTask,
    loading,
    error: taskLoadError,
  } = useTaskDefinition({
    taskId,
    workTaskDocumentId,
    enabled: config !== undefined,
    reloadKey: loadAttempt,
    loadLegacy: true,
  });
  const workTask = configuredWorkTask || orbTask;
  const loadError = Boolean(taskLoadError);
  const sharedDetails = useMemo(
    () =>
      workTask ? getStoredWorkTaskSharedDetails(workTask, stepCaptures) : null,
    [stepCaptures, workTask]
  );

  useEffect(() => {
    if (taskId) setCurrentTaskId(taskId);
  }, [setCurrentTaskId, taskId]);

  useEffect(() => {
    setIsRedoing(redoMode);
  }, [redoMode, stepNumber]);

  useEffect(() => {
    if (privacyModal.accepted === false || !stepNumber || !taskId) {
      navigate(AppRoutes.home);
    } else {
      setInit(true);
    }
  }, [navigate, privacyModal.accepted, stepNumber, taskId]);

  const taskSteps = useMemo(() => workTask?.steps || [], [workTask]);
  const navigationSteps = useMemo(() => {
    if (!navigationEntryId || !workTask) return undefined;
    try {
      return getTaskNavigationSteps(workTask, navigationEntryId);
    } catch {
      return [];
    }
  }, [navigationEntryId, workTask]);
  const currentStep = taskSteps.find(
    (step) => step.stepNumber === stepNumber && step.captureType === captureType
  );
  useEffect(() => {
    if (currentStep?.rtmsSequence && workTask) {
      navigate(getWorkTaskStepRoute(currentStep), {
        replace: true,
        state: getWorkTaskStepRouteState(
          workTask,
          currentStep,
          editMode,
          navigationEntryId
        ),
      });
    }
  }, [currentStep, editMode, navigate, navigationEntryId, workTask]);
  const currentStepDetailFields = useMemo(
    () => (currentStep ? getOrbStepDetailFields(currentStep) : []),
    [currentStep]
  );
  const currentLabelRelations = useMemo(
    () =>
      currentStep && workTask
        ? [...workTask.labelRelations, ...currentStep.labelRelations]
        : [],
    [currentStep, workTask]
  );

  useEffect(() => {
    if (!currentStep) return;

    const restoredValues = getApplicableOrbDetailValues(
      getStepDetailsWithFields(currentStep.stepNumber),
      fieldIds(currentStep)
    );
    latestDetailValuesRef.current = restoredValues;
    setCurrentDetailValues(restoredValues);
    editedStepDetailsRef.current = null;
    validateStepDetailsRef.current = null;
  }, [currentStep, getStepDetailsWithFields]);

  const detailsForStep = useCallback(
    (step: ResolvedWorkTaskStep): OrbDetailValue[] => {
      if (step.stepNumber === stepNumber) {
        return getApplicableOrbDetailValues(
          currentDetailValues,
          fieldIds(step)
        );
      }
      return getApplicableOrbDetailValues(
        getStepDetailsWithFields(step.stepNumber),
        fieldIds(step)
      );
    },
    [currentDetailValues, getStepDetailsWithFields, stepNumber]
  );

  const isStepCompleted = useCallback(
    (selectedStepNumber: number) => {
      if (isRedoing && selectedStepNumber === stepNumber) return false;
      const selectedStep = taskSteps.find(
        (step) => step.stepNumber === selectedStepNumber
      );
      if (!selectedStep) return false;
      if (selectedStep.rtmsSequence) {
        return isWorkTaskStepComplete(
          selectedStep,
          stepCaptures,
          stepDetails,
          sharedDetails
        );
      }
      if (!isQrCaptureType(selectedStep.captureType)) {
        return isWorkTaskStepComplete(
          selectedStep,
          stepCaptures,
          stepDetails,
          sharedDetails
        );
      }
      const savedStep = stepCaptures.find((capture) =>
        isStoredCaptureForWorkTaskStep(selectedStep, capture)
      );
      if (!savedStep?.orbConfirmation || selectedStep.orbExtension === null) {
        return false;
      }
      if (
        savedStep.schemaFingerprint !==
        getWorkTaskStepSchemaFingerprint(selectedStep)
      ) {
        return false;
      }

      return (
        savedStep.orbConfirmation.fingerprint ===
        createOrbConfirmationFingerprint({
          captureTaskId: selectedStep.captureTaskId,
          stepLabel: selectedStep.stepLabel,
          extension: selectedStep.orbExtension,
          storedDetails: sharedDetails,
          stepDetails: [
            ...detailsForStep(selectedStep),
            ...getQrRetryDetail(
              savedStep.orbRetryCount ?? 0,
              selectedStep.captureType
            ),
          ],
        })
      );
    },
    [
      detailsForStep,
      isRedoing,
      sharedDetails,
      stepCaptures,
      stepDetails,
      stepNumber,
      taskSteps,
    ]
  );

  const isStepStarted = useCallback(
    (selectedStepNumber: number) =>
      stepCaptures.some(
        (capture) => capture.stepNumber === selectedStepNumber
      ) || getStepDetails(selectedStepNumber) !== undefined,
    [getStepDetails, stepCaptures]
  );

  const handleStepDetailsChange = useCallback(
    (
      changedStepNumber: number,
      details: string[],
      fields?: StepDetailValue[]
    ) => {
      const nextValues = fields || [];
      if (changedStepNumber === stepNumber) {
        latestDetailValuesRef.current = nextValues;
        setCurrentDetailValues(nextValues);
      }

      const nextDetails = {
        stepNumber: changedStepNumber,
        stepDetails: details,
        stepDetailsFields: fields,
        ...(currentStep?.captureEntryId
          ? { captureEntryId: currentStep.captureEntryId }
          : {}),
      };
      if (isRedoing) {
        editedStepDetailsRef.current = nextDetails;
        return;
      }

      const nextSave = pendingStepDetailsSaveRef.current
        .catch(() => undefined)
        .then(() => addStepDetails(nextDetails));
      pendingStepDetailsSaveRef.current = nextSave;
      nextSave.catch((error) => {
        console.error("Failed to save Orb step details:", error);
        toast.error("Your step details could not be saved. Please try again.");
      });
    },
    [addStepDetails, currentStep?.captureEntryId, isRedoing, stepNumber]
  );

  const handleValidationRegister = useCallback(
    (validate: () => Array<{ id: string; title: string }>) => {
      validateStepDetailsRef.current = validate;
    },
    []
  );

  const saveCurrentStepDraft = useCallback(async () => {
    if (isRedoing) return;
    await pendingStepDetailsSaveRef.current;
  }, [isRedoing]);

  const navigateToStep = useCallback(
    (selectedStep: TaskStep) => {
      if (!taskId || !workTask) return;

      const workTaskStep = selectedStep as ResolvedWorkTaskStep;
      navigate(getWorkTaskStepRoute(workTaskStep), {
        state: {
          ...getWorkTaskStepRouteState(
            workTask,
            workTaskStep,
            editMode,
            navigationSteps?.some(
              (step) => step.stepNumber === workTaskStep.stepNumber
            )
              ? navigationEntryId
              : undefined
          ),
          reviewCompletedSteps,
        },
      });
    },
    [
      editMode,
      navigate,
      navigationEntryId,
      navigationSteps,
      reviewCompletedSteps,
      taskId,
      workTask,
    ]
  );

  const handleNavigationSaveError = useCallback((error: unknown) => {
    console.error("Failed to save Orb step before switching:", error);
    toast.error("Your current step could not be saved. Please try again.");
  }, []);

  const {
    completedSteps,
    completionPercentage,
    nextIncompleteStep,
    previousStep,
    selectPreviousStep: handlePreviousStep,
    selectStep: handleSelectStep,
  } = useStepNavigation({
    steps: taskSteps,
    currentStepNumber: stepNumber,
    isStepCompleted,
    saveCurrentStepDraft,
    navigateToStep,
    onSaveError: handleNavigationSaveError,
  });

  const currentConfiguredDetails = useMemo(
    () =>
      currentStep
        ? getApplicableOrbDetailValues(
            currentDetailValues,
            fieldIds(currentStep)
          )
        : [],
    [currentDetailValues, currentStep]
  );
  const currentSavedStep = currentStep
    ? stepCaptures.find((capture) =>
        isStoredCaptureForWorkTaskStep(currentStep, capture)
      )
    : undefined;
  const currentRetryCount =
    (currentSavedStep?.orbRetryCount ?? 0) + (isRedoing ? 1 : 0);

  const handleBackToReview = useCallback(() => {
    if (!workTask) return;
    if (editMode && isRedoing) {
      navigate(AppRoutes.multiStepUpload, {
        state: {
          taskId: workTask.workTaskId,
          taskName: workTask.workTaskName,
        },
      });
      return;
    }
    const destination = getWorkTaskReviewDestination(workTask, isStepCompleted);
    navigate(destination.route, { state: destination.state });
  }, [editMode, isRedoing, isStepCompleted, navigate, workTask]);
  const currentQrDetails = useMemo(
    () => [
      ...currentConfiguredDetails,
      ...getQrRetryDetail(currentRetryCount, captureType),
      ...(currentStep ? getOrbStepLabelDetails(currentStep.stepLabel) : []),
    ],
    [currentConfiguredDetails, currentRetryCount, currentStep, captureType]
  );
  const currentValueById = useMemo(
    () =>
      Object.fromEntries(
        currentConfiguredDetails.map((field) => [field.label_id, field.value])
      ),
    [currentConfiguredDetails]
  );
  const hasMissingRequiredDetails = Boolean(
    currentStepDetailFields.some((field) => {
      const id = field.label_id?.toString().trim() || "";
      return field.is_required && !currentValueById[id]?.trim();
    })
  );

  const { content: generatedQrContent, error: qrError } = useOrbQrContent({
    captureTaskId: currentStep?.captureTaskId,
    extension: currentStep?.orbExtension ?? null,
    storedDetails: sharedDetails,
    stepDetails: currentQrDetails,
    includeDetails: true,
    detailsLoaded: !loading,
    sessionMetadata:
      !isRedoing && currentSavedStep
        ? currentSavedStep.sessionMetadata || {}
        : undefined,
  });
  const qrContent = hasMissingRequiredDetails ? null : generatedQrContent;
  const allStepsCompleted =
    taskSteps.length > 0 &&
    taskSteps.every((step) => isStepCompleted(step.stepNumber));
  const currentStepCompleted =
    !isRedoing && currentStep ? isStepCompleted(currentStep.stepNumber) : false;
  const showDistributorQr =
    !currentStepCompleted &&
    Boolean(distributorId) &&
    shownDistributor !== distributorKey;

  const handleCompleteCapture = useCallback(async () => {
    if (!currentStep || currentStep.orbExtension === null || showDistributorQr)
      return;

    const missingFields = validateStepDetailsRef.current?.() || [];
    if (missingFields.length > 0) {
      document
        .getElementById(`step-detail-${missingFields[0].id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!generatedQrContent) {
      toast.error("The QR code is not ready yet. Please try again.");
      return;
    }

    setCaptureSaving(true);
    const previousDetails: StepDetailsData = {
      stepNumber: currentStep.stepNumber,
      stepDetails: getStepDetails(currentStep.stepNumber) || [],
      stepDetailsFields: getStepDetailsWithFields(currentStep.stepNumber),
    };
    let editedDetailsSaved = false;
    try {
      await pendingStepDetailsSaveRef.current;
      const editedDetails = editedStepDetailsRef.current;
      if (editedDetails) {
        await addStepDetails(editedDetails);
        editedDetailsSaved = true;
      }
      const applicableDetails = getApplicableOrbDetailValues(
        latestDetailValuesRef.current,
        fieldIds(currentStep)
      );
      const retryCount = currentRetryCount;
      await addStepCapture({
        stepNumber: currentStep.stepNumber,
        stepLabel: currentStep.stepLabel,
        stepTitle: currentStep.stepTitle,
        numberOfCaptures: currentStep.numberOfCaptures,
        captureTaskId: currentStep.captureTaskId,
        ...(currentStep.captureEntryId
          ? { captureEntryId: currentStep.captureEntryId }
          : {}),
        captureTaskStepNumber: currentStep.captureTaskStepNumber,
        captureType: currentStep.captureType,
        schemaFingerprint: getWorkTaskStepSchemaFingerprint(currentStep),
        files: [],
        isComplete: true,
        orbRetryCount: retryCount,
        orbConfirmation: {
          fingerprint: createOrbConfirmationFingerprint({
            captureTaskId: currentStep.captureTaskId,
            stepLabel: currentStep.stepLabel,
            extension: currentStep.orbExtension,
            storedDetails: sharedDetails,
            stepDetails: [
              ...applicableDetails,
              ...getQrRetryDetail(retryCount, captureType),
            ],
          }),
          confirmedAt: Date.now(),
        },
      });
      editedStepDetailsRef.current = null;
      setIsRedoing(false);
    } catch (error) {
      if (editedDetailsSaved) {
        try {
          await addStepDetails(previousDetails);
        } catch (restoreError) {
          console.error(
            "Failed to restore previous Orb step details:",
            restoreError
          );
          toast.error(
            "The previous capture details could not be restored. Do not continue; contact support."
          );
          setCaptureSaving(false);
          return;
        }
      }
      console.error("Failed to save Orb capture confirmation:", error);
      toast.error("Capture confirmation could not be saved. Please try again.");
      setCaptureSaving(false);
      return;
    }
    setCaptureSaving(false);

    const nextStep =
      (navigationSteps || taskSteps).find(
        (step) =>
          step.stepNumber > currentStep.stepNumber &&
          !isStepCompleted(step.stepNumber)
      ) ||
      (navigationSteps
        ? navigationSteps.find(
            (step) =>
              step.stepNumber !== currentStep.stepNumber &&
              !isStepCompleted(step.stepNumber)
          )
        : nextIncompleteStep);
    if (nextStep) {
      navigateToStep(nextStep);
    } else if (navigationEntryId) {
      toast.success("Task position saved. Your other progress is preserved.");
      navigate(AppRoutes.home);
    } else if (workTask?.hasUploadSteps) {
      navigate(AppRoutes.multiStepUpload, {
        state: {
          taskId,
          taskName: workTask.workTaskName,
        },
      });
    } else if (workTask && reviewCompletedSteps) {
      navigate(getWorkTaskStepRoute(currentStep), {
        state: getWorkTaskStepRouteState(workTask, currentStep, editMode),
      });
    }
  }, [
    captureType,
    addStepCapture,
    addStepDetails,
    currentStep,
    currentRetryCount,
    generatedQrContent,
    getStepDetails,
    getStepDetailsWithFields,
    navigate,
    navigateToStep,
    nextIncompleteStep,
    navigationEntryId,
    navigationSteps,
    isStepCompleted,
    taskSteps,
    editMode,
    reviewCompletedSteps,
    sharedDetails,
    showDistributorQr,
    taskId,
    workTask,
  ]);

  const handleRedoCapture = useCallback(() => {
    if (!currentStep || !currentStepCompleted) return;
    if (
      allStepsCompleted &&
      workTask &&
      !workTask.hasUploadSteps &&
      !reviewCompletedSteps
    ) {
      const firstStep = workTask.steps[0];
      navigate(getWorkTaskStepRoute(firstStep), {
        state: {
          ...getWorkTaskStepRouteState(workTask, firstStep),
          reviewCompletedSteps: true,
        },
      });
      return;
    }
    setShownDistributor(undefined);
    setIsRedoing(true);
  }, [
    allStepsCompleted,
    currentStep,
    currentStepCompleted,
    navigate,
    reviewCompletedSteps,
    workTask,
  ]);

  const handleSaveProgress = useCallback(async () => {
    setProgressActionPending(true);
    try {
      await saveCurrentStepDraft();
      setShowSaveModal(false);
      navigate(AppRoutes.home);
    } catch (error) {
      console.error("Failed to save Orb progress before leaving:", error);
      toast.error("Your progress could not be saved. Please try again.");
    } finally {
      setProgressActionPending(false);
    }
  }, [navigate, saveCurrentStepDraft]);

  const handleDiscardProgress = useCallback(async () => {
    setProgressActionPending(true);
    try {
      await clearAllCaptures();
      setShowSaveModal(false);
      navigate(AppRoutes.home);
    } catch (error) {
      console.error("Failed to delete Orb task progress:", error);
      toast.error("Your progress could not be deleted. Please try again.");
    } finally {
      setProgressActionPending(false);
    }
  }, [clearAllCaptures, navigate]);

  const handleFinish = useCallback(async () => {
    if (!taskId || !allStepsCompleted) return;
    setProgressActionPending(true);
    try {
      await saveCompletedTaskSteps(
        taskId,
        contributor,
        getCompletedTaskSteps({
          flow: workTask,
          instructions: null,
          captureType,
          captures: stepCaptures,
          details: stepDetails,
        })
      );
      await clearAllCaptures();
      navigate(AppRoutes.home);
    } catch (error) {
      console.error("Could not finish Orb task:", error);
      toast.error(
        "Task completion could not be saved. Your progress is preserved; please retry Finish."
      );
    } finally {
      setProgressActionPending(false);
    }
  }, [
    captureType,
    taskId,
    contributor,
    workTask,
    stepCaptures,
    stepDetails,
    allStepsCompleted,
    clearAllCaptures,
    navigate,
  ]);

  if (!init || currentStep?.rtmsSequence) return null;

  if (
    loading ||
    config === undefined ||
    currentTaskId !== taskId ||
    isLoadingSteps
  ) {
    return (
      <Layout className="space-y-8">
        <div className="flex min-h-[240px] items-center justify-center">
          <p className="text-858494">Loading step...</p>
        </div>
      </Layout>
    );
  }

  if (
    loadError ||
    !workTask ||
    !currentStep ||
    navigationSteps?.length === 0 ||
    hasChangedTaskPositions(workTask, [...stepCaptures, ...stepDetails])
  ) {
    return (
      <Layout className="space-y-8">
        <div className="space-y-4 text-center">
          <h1 className="text-24 font-semibold">Step unavailable</h1>
          <p className="text-858494">
            {taskLoadError instanceof Error
              ? taskLoadError.message
              : "We could not load this step. Your saved progress has not changed."}
          </p>
          <Button onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
            Try Again
          </Button>
          <Button to={AppRoutes.home}>Back to Homepage</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      key={stepNumber}
      className="space-y-8"
      toolbarClassName="z-[100] !py-3"
      toolbar={
        <>
          <div
            className={
              allStepsCompleted && !workTask.hasUploadSteps
                ? "grid grid-cols-2 gap-3"
                : "contents"
            }
          >
            {currentStepCompleted ? (
              <Button onClick={handleRedoCapture} disabled={captureSaving}>
                {captureSaving ? "Saving..." : "Redo capture"}
              </Button>
            ) : showDistributorQr ? (
              <Button
                onClick={() => setShownDistributor(distributorKey)}
                disabled={!distributorQrContent || hasMissingRequiredDetails}
              >
                Shown to the Orb
              </Button>
            ) : (
              <div className="flex gap-3">
                {shownDistributor === distributorKey && distributorId && (
                  <Button
                    onClick={() => setShownDistributor(undefined)}
                    variant={ButtonVariant.FLAT_BORDER}
                    aria-label="Back to distributor QR code"
                    disabled={captureSaving}
                  >
                    ←
                  </Button>
                )}
                <Button
                  className="flex-1"
                  onClick={handleCompleteCapture}
                  disabled={
                    captureSaving || !qrContent || hasMissingRequiredDetails
                  }
                >
                  {captureSaving ? "Saving..." : "Capture complete"}
                </Button>
              </div>
            )}
            {allStepsCompleted ? (
              workTask.hasUploadSteps ? (
                <Button
                  onClick={handleBackToReview}
                  variant={ButtonVariant.FLAT_BORDER}
                >
                  Review captures
                </Button>
              ) : (
                <Button
                  onClick={handleFinish}
                  variant={ButtonVariant.FLAT_BORDER}
                  disabled={progressActionPending || captureSaving}
                >
                  Finish
                </Button>
              )
            ) : null}
          </div>
          {!allStepsCompleted && (
            <button
              onClick={() => setShowSaveModal(true)}
              className="text-sm text-858494 underline hover:text-191c20"
            >
              Back to Homepage
            </button>
          )}
        </>
      }
    >
      <SaveProgressModal
        show={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveProgress}
        onDiscard={handleDiscardProgress}
        busy={progressActionPending}
      />

      <div className="space-y-6">
        {editMode ? (
          <button
            onClick={handleBackToReview}
            className="flex items-center gap-2 text-3461fd hover:text-3461fd/80"
          >
            ← Back to final review
          </button>
        ) : (
          previousStep && (
            <button
              onClick={handlePreviousStep}
              className="flex items-center gap-2 text-3461fd hover:text-3461fd/80"
            >
              ← Back to Step {previousStep.stepNumber}
            </button>
          )
        )}

        {taskSteps.length > 1 && (
          <div className="space-y-2">
            <div className="h-2 w-full rounded-full bg-f4f7ff">
              <div
                className="h-2 rounded-full bg-3461fd transition-all duration-300"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-858494">
              <span>
                {completedSteps} of {taskSteps.length} complete
              </span>
              <span>{completionPercentage}% complete</span>
            </div>
          </div>
        )}

        <StepProgressOverview
          steps={taskSteps}
          currentStepNumber={stepNumber}
          isStepCompleted={isStepCompleted}
          isStepStarted={isStepStarted}
          onSelectStep={handleSelectStep}
        />

        <section
          className="space-y-4"
          aria-label={`Step ${stepNumber} instructions`}
        >
          {currentStep.instructionSlices.length > 0 ? (
            <SliceZone
              slices={currentStep.instructionSlices}
              components={taskInstructionSliceComponents}
              context={{
                orbStepLabel:
                  taskSteps.length > 1
                    ? currentStep.stepLabelDisplayName || currentStep.stepLabel
                    : undefined,
              }}
            />
          ) : (
            <div className="text-center space-y-2">
              <h1 className="text-24 font-semibold">{currentStep.stepTitle}</h1>
              {taskSteps.length > 1 && (
                <h2 className="text-lg font-medium text-3461fd">
                  {currentStep.stepLabelDisplayName || currentStep.stepLabel}
                </h2>
              )}
            </div>
          )}
        </section>

        {(currentStepDetailFields.length > 0 ||
          currentStep.stepLabels?.length) && (
          <section className="space-y-4" aria-label="Capture details">
            <StepDetails
              heading="Capture details"
              description="For this capture step"
              stepNumber={stepNumber}
              stepDetailsFields={currentStepDetailFields}
              dropdownValueSide="value"
              onValidationRegister={handleValidationRegister}
              onStepDetailsChange={handleStepDetailsChange}
              getStepDetails={getStepDetails}
              taskId={taskId}
              loadUserProfile={apiGetUserProfile}
              labelRelations={currentLabelRelations}
              disabled={currentStepCompleted}
              stepLabels={currentStep.stepLabels}
            />
          </section>
        )}

        <section
          className="space-y-5 text-center"
          aria-label={`${captureLabel} QR code`}
        >
          {showDistributorQr ? (
            <h2 className="text-lg font-semibold">Distributor QR code</h2>
          ) : (
            <div className="grid gap-1 justify-center font-medium">
              <p>Signed in as</p>
              <span className="bg-858494/10 rounded-[20px] px-3 py-2">
                {getUserEmail()}
              </span>
            </div>
          )}

          {currentStepCompleted ? (
            <p className="text-858494">
              Capture complete. Select “Redo capture” to unlock the details and
              generate a new QR code.
            </p>
          ) : hasMissingRequiredDetails ? (
            <p className="text-858494">
              Complete the required capture details to generate this QR code.
            </p>
          ) : showDistributorQr ? (
            distributorQrContent ? (
              <OrbQRCode content={distributorQrContent} />
            ) : (
              <p className="text-fd380f" role="alert">
                The collection's distributor ID is invalid. Contact your
                collection coordinator.
              </p>
            )
          ) : qrError ? (
            <p className="text-fd380f">{qrError}</p>
          ) : qrContent ? (
            <OrbQRCode
              content={qrContent}
              label={`${captureLabel} capture QR code`}
            />
          ) : (
            <p className="text-858494">Generating QR code...</p>
          )}

          {!currentStepCompleted && (
            <p className="text-sm text-858494">
              {showDistributorQr
                ? "Show this distributor QR code to the Orb first, then select “Shown to the Orb”."
                : captureType === "rtms"
                ? "Hold this QR code up to your computer’s webcam during the Zoom call. Keep it visible until the collection operator confirms it has been scanned."
                : "Show this QR code to the Orb for this capture step."}
            </p>
          )}

          {captureType === "rtms" && !currentStepCompleted && (
            <p className="text-sm text-858494">
              After scanning, follow the task instructions and keep your face
              visible. Select “Capture complete” only when the collection
              operator confirms this step is finished.
            </p>
          )}

          {allStepsCompleted && !workTask.hasUploadSteps && (
            <div className="rounded-xl bg-f4f7ff p-4 text-191c20">
              All capture steps are complete.
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
